/**
 * Multicam: line up several camera angles (by embedded timecode, or by their
 * starts), watch them side by side in sync, and cut between them live, by
 * clicking an angle or pressing its number while playing. The cuts become
 * storyline clips that remember their group, so any cut can later switch to
 * another angle at the same moment from the inspector.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Film, Pause, Play, Plus, Scissors, Search, Trash2, X } from 'lucide-react';
import {
  TICKS_PER_SECOND, formatDuration, framesToTimecode, formatTimecode, layoutVideo, multicamDuration, ops, parseTimecode, snap, syncAngles, ticksPerFrame, ticksToFrames,
  type AssetDetail, type MulticamCut, type MulticamGroup, type SequenceDocument,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useLibrary, type SequenceSource } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { isTyping } from '../../lib/keys';

const S = TICKS_PER_SECOND;

export function MulticamDialog({ doc, apply, sources, playhead, onClose }: {
  doc: SequenceDocument;
  apply: (fn: (d: SequenceDocument) => SequenceDocument) => void;
  sources: Map<string, SequenceSource>;
  playhead: number;
  onClose: () => void;
}) {
  const groups = doc.multicam ?? [];
  const [groupId, setGroupId] = useState<string | null>(groups.at(-1)?.id ?? null);
  const [creating, setCreating] = useState(groups.length === 0);
  // A group just created is shown as soon as it is in the document.
  const group = groups.find((g) => g.id === groupId) ?? groups.at(-1) ?? null;
  return (
    <Modal title="Multicam" wide onClose={onClose} actions={<button className="btn" onClick={onClose}>Close</button>}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {groups.map((g) => (
          <button key={g.id} className={`tag toggle ${g.id === group?.id && !creating ? 'on' : ''}`} onClick={() => { setGroupId(g.id); setCreating(false); }} data-testid="multicam-group">
            {g.label} <span className="faint">· {g.angles.length} angles</span>
          </button>
        ))}
        <button className={`tag toggle ${creating ? 'on' : ''}`} onClick={() => setCreating(true)} data-testid="new-multicam"><Plus size={12} /> New multicam clip</button>
      </div>
      {creating
        ? <NewGroup doc={doc} onCreate={(g) => { apply((d) => ops.addMulticam(d, g)); setCreating(false); setGroupId(null); }} />
        : group ? <GroupEditor key={group.id} doc={doc} group={group} apply={apply} sources={sources} playhead={playhead} onDone={onClose} />
        : <p className="muted">Pick a multicam clip above, or make a new one.</p>}
    </Modal>
  );
}

/** Pick the angles and how to line them up. */
function NewGroup({ doc, onCreate }: { doc: SequenceDocument; onCreate: (g: Omit<MulticamGroup, 'id'>) => void }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [by, setBy] = useState<'timecode' | 'start'>('timecode');
  const [busy, setBusy] = useState(false);
  const lib = useLibrary({ q, status: ['ready', 'partial'], kind: ['video'], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false });
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 9 ? p : [...p, id]));

  async function create() {
    setBusy(true);
    try {
      const assets = (await Promise.all(picked.map((id) => api<AssetDetail>(`/api/v1/assets/${id}`)))).filter((a) => a.versionId);
      const sources = assets.map((a) => {
        const rate = a.technical?.frameRate ?? doc.frameRate;
        const frames = a.timing.startTimecode ? parseTimecode(a.timing.startTimecode, rate) : null;
        return { assetId: a.id, versionId: a.versionId!, label: a.title, durationTicks: a.durationTicks ?? 0, startTimecodeTicks: frames == null ? null : Math.round(frames * ticksPerFrame(rate)) };
      });
      const synced = syncAngles(sources, doc.frameRate, by);
      if (by === 'timecode' && synced.syncedBy === 'start') toast('Not every angle has timecode, so they are lined up by their starts', 'info');
      const span = Math.max(...synced.angles.map((a) => a.startTicks + a.durationTicks)) - Math.min(...synced.angles.map((a) => a.startTicks));
      if (synced.syncedBy === 'timecode' && span > 6 * 3600 * S) toast('These angles’ timecodes are hours apart: check that they are from the same shoot', 'info');
      onCreate({ label: label.trim() || `Multicam ${(doc.multicam ?? []).length + 1}`, ...synced });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="new-multicam-form">
      <div className="field"><label htmlFor="mc-name">Name</label><input id="mc-name" className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Interview, three cameras" /></div>
      <div className="field">
        <label>Angles <span className="faint">({picked.length} of up to 9, in camera order)</span></label>
        <div className="search" style={{ width: 'auto' }}><Search size={14} /><input className="input" placeholder="Search video…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search video" /></div>
        <div className="multicam-picker">
          {lib.data?.items.map((a) => {
            const n = picked.indexOf(a.id);
            return (
              <button key={a.id} type="button" className={`multicam-pick ${n >= 0 ? 'on' : ''}`} onClick={() => toggle(a.id)} data-testid="multicam-pick" data-title={a.title}>
                <span className="n">{n >= 0 ? n + 1 : ''}</span>
                {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <Film size={16} />}
                <span className="t">{a.title}</span>
                <span className="faint mono" style={{ fontSize: 11 }}>{a.durationTicks ? formatDuration(a.durationTicks) : ''}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="field">
        <label>Line the angles up by</label>
        <div className="segmented" role="radiogroup">
          <button aria-pressed={by === 'timecode'} onClick={() => setBy('timecode')}>Timecode</button>
          <button aria-pressed={by === 'start'} onClick={() => setBy('start')}>Their starts</button>
        </div>
        <span className="help">Timecode works when the cameras recorded the same time of day (jam-synced or fed one timecode). Otherwise start them together, or trim them to a clap first.</span>
      </div>
      <button className="btn primary" style={{ alignSelf: 'flex-start' }} disabled={picked.length < 2 || busy} onClick={() => void create()} data-testid="create-multicam">Create multicam clip</button>
    </div>
  );
}

/** Watch the angles in sync and cut between them. */
function GroupEditor({ doc, group, apply, sources, playhead, onDone }: {
  doc: SequenceDocument; group: MulticamGroup; apply: (fn: (d: SequenceDocument) => SequenceDocument) => void;
  sources: Map<string, SequenceSource>; playhead: number; onDone: () => void;
}) {
  const duration = multicamDuration(group);
  const tpf = ticksPerFrame(doc.frameRate);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [angle, setAngle] = useState(0);
  const [recording, setRecording] = useState(true);
  const [cuts, setCuts] = useState<MulticamCut[]>([]);
  const [range, setRange] = useState<{ in: number; out: number }>({ in: 0, out: duration });
  const [audio, setAudio] = useState<number | 'follow'>(0);
  const [where, setWhere] = useState<'append' | 'playhead'>('append');
  const videos = useRef<Array<HTMLVideoElement | null>>([]);
  const clock = useRef({ t: 0 });
  clock.current.t = t;
  const live = useRef(0);
  live.current = angle;
  const tc = (ticks: number) => formatTimecode(framesToTimecode(ticksToFrames(ticks, doc.frameRate), doc.frameRate));

  const cutTo = (k: number) => {
    if (!group.angles[k]) return;
    setAngle(k);
    if (recording) {
      const at = snap(clock.current.t, doc.frameRate);
      setCuts((c) => [...c.filter((x) => x.atTicks !== at), { atTicks: at, angle: k }].sort((a, b) => a.atTicks - b.atTicks));
    }
  };

  // While stopped, every angle shows the frame at the group's clock (scrubbing).
  useEffect(() => {
    group.angles.forEach((a, k) => {
      const v = videos.current[k];
      if (!v) return;
      v.muted = audio === 'follow' ? k !== angle : k !== audio;
      if (playing) return;
      const local = (t - a.startTicks) / S;
      const covered = local >= 0 && local < a.durationTicks / S;
      v.style.visibility = covered ? 'visible' : 'hidden';
      v.pause();
      v.playbackRate = 1;
      if (covered && Math.abs(v.currentTime - local) > 0.03) v.currentTime = local;
    });
  }, [t, playing, group, audio, angle]);

  const playRequests = useRef(new Set<HTMLVideoElement>());
  const [waitingFor, setWaitingFor] = useState<number | null>(null);

  /**
   * Steers every angle to the clock while playing, and says which angle the
   * clock must wait for. Several streams at once often stall one of them: the
   * clock then holds (the others pause) instead of seeking the slow one over
   * and over, which kept it buffering for good. Small drift is taken up by
   * playing a little faster or slower; only large drift seeks.
   */
  const steer = (now: number, patience: Map<number, number>): number | null => {
    let blocking: number | null = null;
    const ready: Array<[HTMLVideoElement, number]> = [];
    group.angles.forEach((a, k) => {
      const v = videos.current[k];
      if (!v) return;
      const local = (now - a.startTicks) / S;
      const covered = local >= 0 && local < a.durationTicks / S;
      v.style.visibility = covered ? 'visible' : 'hidden';
      if (!covered) { if (!v.paused) v.pause(); patience.delete(k); return; }
      const far = Math.abs(v.currentTime - local) > 1;
      if (far && !v.seeking) v.currentTime = local;
      const stalled = v.seeking || v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
      if (stalled) {
        // Keep it fetching; give up waiting on an angle after a few seconds so one bad stream cannot stop the others.
        if (!patience.has(k)) patience.set(k, performance.now());
        if (performance.now() - patience.get(k)! < 5000 && blocking == null) blocking = k;
        play(v);
      } else {
        patience.delete(k);
        ready.push([v, local]);
      }
    });
    for (const [v, local] of ready) {
      if (blocking != null) { if (!v.paused) v.pause(); continue; }
      const drift = v.currentTime - local;
      v.playbackRate = Math.abs(drift) < 0.04 ? 1 : 1 - Math.max(-0.1, Math.min(0.1, drift * 0.5));
      play(v);
    }
    return blocking;
  };
  const play = (v: HTMLVideoElement) => {
    if (!v.paused || playRequests.current.has(v)) return;
    playRequests.current.add(v);
    v.play().catch((err: Error) => {
      // A browser that will not start sound without a click still plays the picture.
      if (err.name === 'NotAllowedError' && !v.muted) { v.muted = true; toast('The browser blocked the sound: click Play again to hear it', 'info'); }
    }).finally(() => playRequests.current.delete(v));
  };

  useEffect(() => {
    if (!playing) return;
    // Recording from here: the angle on screen now is where the edit starts.
    if (recording) {
      const at = snap(clock.current.t >= duration ? 0 : clock.current.t, doc.frameRate);
      setCuts((c) => (c.some((x) => x.atTicks <= at) ? c : [...c, { atTicks: at, angle }].sort((a, b) => a.atTicks - b.atTicks)));
    }
    let start = performance.now();
    let base = clock.current.t >= duration ? 0 : clock.current.t;
    let held: number | null = null;
    const patience = new Map<number, number>();
    let raf = 0;
    const tick = () => {
      let now = base + Math.round(((performance.now() - start) / 1000) * S);
      // The clock follows the live angle's picture (or another playing angle) so what is seen and the timecode agree.
      const leader = [live.current, ...group.angles.keys()].find((k) => {
        const v = videos.current[k];
        return v && !v.paused && !v.seeking && v.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA && v.style.visibility !== 'hidden';
      });
      if (leader != null) now = group.angles[leader]!.startTicks + Math.round(videos.current[leader]!.currentTime * S);
      if (now >= duration) { setT(duration); setPlaying(false); return; }
      const blocking = steer(now, patience);
      // Hold the clock until the angle catches up.
      if (blocking != null) now = base;
      base = now;
      start = performance.now();
      if (blocking !== held) { held = blocking; setWaitingFor(blocking); }
      setT(now);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      setWaitingFor(null);
      videos.current.forEach((v) => { if (v) { v.pause(); v.playbackRate = 1; } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, duration]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e, 'input, textarea, select') || e.metaKey || e.ctrlKey) return;
      if (/^[1-9]$/.test(e.key)) { e.preventDefault(); e.stopPropagation(); cutTo(Number(e.key) - 1); }
      else if (e.key === ' ') { e.preventDefault(); e.stopPropagation(); setPlaying((p) => !p); }
      else if (e.key.toLowerCase() === 'i') { e.preventDefault(); e.stopPropagation(); setRange((r) => ({ ...r, in: Math.min(snap(clock.current.t, doc.frameRate), r.out - tpf) })); }
      else if (e.key.toLowerCase() === 'o') { e.preventDefault(); e.stopPropagation(); setRange((r) => ({ ...r, out: Math.max(snap(clock.current.t, doc.frameRate), r.in + tpf) })); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const inRange = cuts.filter((c) => c.atTicks >= range.in && c.atTicks < range.out);
  const columns = group.angles.length <= 4 ? 2 : 3;
  const preview = useMemo(() => ops.cutMulticam({ ...doc, video: [], audio: [] }, group.id, cuts.length ? cuts : [{ atTicks: range.in, angle }], range.in, range.out, { audio }).video.length, [doc, group.id, cuts, range, angle, audio]);

  function commit() {
    const list = cuts.length ? cuts : [{ atTicks: range.in, angle }];
    apply((d) => {
      // At the playhead: split the storyline there and insert between the halves.
      const base = where === 'playhead' ? ops.splitAt(d, playhead) : d;
      const next = where === 'playhead' ? layoutVideo(base).findIndex((p) => p.recordIn >= playhead) : -1;
      return ops.cutMulticam(base, group.id, list, range.in, range.out, { index: next < 0 ? base.video.length : next, audio });
    });
    toast(`Cut “${group.label}” into the storyline`);
    onDone();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="multicam-editor">
      <div className="multicam-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {group.angles.map((a, k) => {
          const url = sources.get(a.versionId)?.proxyUrl;
          return (
            <button key={k} type="button" className={`multicam-angle ${angle === k ? 'live' : ''}`} onClick={() => cutTo(k)} data-testid={`multicam-angle-${k + 1}`}
              style={{ aspectRatio: `${doc.width} / ${doc.height}` }}>
              {url ? <video ref={(el) => { videos.current[k] = el; }} src={url} playsInline preload="auto" /> : <span className="faint">Loading…</span>}
              <span className="angle-label"><strong>{k + 1}</strong> {a.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn icon sm" aria-label={playing ? 'Pause' : 'Play'} onClick={() => setPlaying((p) => !p)} data-testid="multicam-play">{playing ? <Pause size={14} /> : <Play size={14} />}</button>
        <span className="mono tc-small" data-testid="multicam-tc">{tc(t)}</span>
        <input type="range" min={0} max={duration} step={tpf} value={t} onChange={(e) => { setPlaying(false); setT(Number(e.target.value)); }} style={{ flex: 1 }} aria-label="Multicam position" />
        <span className="mono faint tc-small">{tc(duration)}</span>
        {waitingFor != null && <span className="faint" style={{ fontSize: 12 }} data-testid="multicam-buffering">Waiting for angle {waitingFor + 1}…</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn sm ${recording ? 'danger' : ''}`} onClick={() => setRecording((r) => !r)} aria-pressed={recording} title="While on, choosing an angle records a cut at that moment">
          <Circle size={11} fill={recording ? 'currentColor' : 'none'} /> {recording ? 'Recording cuts' : 'Record cuts'}
        </button>
        <span className="faint" style={{ fontSize: 12 }}>Play and press <kbd>1</kbd>–<kbd>{Math.min(9, group.angles.length)}</kbd> or click an angle to cut to it. <kbd>I</kbd> / <kbd>O</kbd> mark the range.</span>
      </div>
      <div className="multicam-cuts" data-testid="multicam-cuts">
        <span className="faint">Range {tc(range.in)} – {tc(range.out)}</span>
        {inRange.length === 0 && <span className="faint">· no cuts yet: the whole range uses angle {angle + 1}</span>}
        {inRange.map((c) => (
          <span key={c.atTicks} className="tag" data-testid="multicam-cut">
            {tc(c.atTicks)} → {c.angle + 1}
            <button className="tl-mini" aria-label="Remove cut" onClick={() => setCuts((x) => x.filter((y) => y.atTicks !== c.atTicks))}><X size={10} /></button>
          </span>
        ))}
        {cuts.length > 0 && <button className="btn ghost sm" onClick={() => setCuts([])}>Clear cuts</button>}
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="mc-audio">Sound</label>
          <select id="mc-audio" className="select" value={String(audio)} onChange={(e) => setAudio(e.target.value === 'follow' ? 'follow' : Number(e.target.value))}>
            {group.angles.map((a, k) => <option key={k} value={k}>{k + 1} · {a.label} throughout (on A2)</option>)}
            <option value="follow">Each angle’s own sound</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="mc-where">Put the edit</label>
          <select id="mc-where" className="select" value={where} onChange={(e) => setWhere(e.target.value as 'append' | 'playhead')}>
            <option value="append">At the end of the storyline</option>
            <option value="playhead">At the playhead</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" onClick={commit} disabled={!preview} data-testid="multicam-commit"><Scissors size={14} /> Cut into storyline ({preview} clip{preview === 1 ? '' : 's'})</button>
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm danger" onClick={() => { if (window.confirm(`Remove the multicam clip “${group.label}”? Clips already cut from it stay in the storyline.`)) apply((d) => ops.removeMulticam(d, group.id)); }}>
          <Trash2 size={13} /> Remove
        </button>
      </div>
    </div>
  );
}
